import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { FileQuestion, Files, Bookmark, FilePen } from 'lucide-react';
import PageHeader from '../../components/headers/Header';
import HRHandbookPage from './hrHandbook';
import OfficePoliciesPage from './officePoliciesPage';
import AddPolicyPage from './hrHandbook/addPolicy';
import PolicyTypesComponent from './def';
import {
  useMediaQuery,
  Fab,
  styled,
  keyframes,
} from '@mui/material';

const IsEmployee = localStorage.getItem('userRole') === 'EMPLOYEE';

// === ANIMATIONS ===
const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
`;

// === STYLED BUTTON — In-line, same height as tabs, no absolute positioning ===
const StyledAddButton = styled(Fab)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main || '#5D40D1'})`,
  color: '#fff',
  fontWeight: '500',
  fontSize: '0.95rem',
  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
  border: 'none',
  borderRadius: '10px', // Match tab buttons
  padding: '10px 20px', // Match tab buttons
  minWidth: '160px', // Fits “Add New Policy” nicely
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  textTransform: 'none',

  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark || '#4A2CB8'})`,
    boxShadow: '0 6px 16px rgba(33, 150, 243, 0.4)',
    // → No transform or movement ←
    '& .policy-icon': {
      animation: `${pulseAnimation} 0.6s ease-in-out`,
    },
  },

  '&:focus-visible': {
    outline: '2px solid #fff',
    outlineOffset: '2px',
    boxShadow: '0 0 0 4px rgba(33, 150, 243, 0.4)',
  },

  '& .MuiTouchRipple-root .MuiTouchRipple-child': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },

  // Ensure it doesn’t elevate above other buttons
  transform: 'none !important',
}));

const PoliciesManagement = () => {

  const location = useLocation();
  const initialTab = location.state?.activeTab || 'opolicies';
  const [activeTab, setActiveTab] = useState(initialTab);

  const isLargeScreen = useMediaQuery('(min-width: 1600px)');

  // Define tabs
  const tabs = [
    // { id: 'def', name: 'Chat', icon: FileQuestion },
    { id: 'opolicies', name: 'Office Policies', icon: Files },
    ...(!IsEmployee ? [{ id: 'hrHandbook', name: 'HR Handbook', icon: Bookmark }] : []),
  ];

  return (
    <div className="Leaves-main-container">
      <div className="Leaves-content-wrapper">
        {/* Header */}
        <PageHeader
          title={!IsEmployee ? 'Policies Management' : 'Office Policies'}
          subtitle={
            !IsEmployee
              ? "Access and manage your organization's policies with intelligent assistance"
              : 'Detailed guidelines governing daily workplace operations'
          }
        />

        {/* Tab Bar — Now includes Add Button inline */}
        <div
          className="Leaves-nav-tabs"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
            padding: '12px 0',
          }}
        >
          {/* Regular Tabs */}
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`Leaves-tab-btn ${activeTab === tab.id ? 'Leaves-tab-btn-active' : ''}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '10px',
                background: activeTab === tab.id ? '#1976d2' : '#f5f5f5',
                color: activeTab === tab.id ? '#fff' : '#333',
                fontWeight: activeTab === tab.id ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.25s ease',
                fontSize: '0.95rem',
                boxShadow: activeTab === tab.id
                  ? '0 3px 10px rgba(25, 118, 210, 0.3)'
                  : '0 2px 5px rgba(0,0,0,0.06)',
                whiteSpace: 'nowrap',
              }}
            >
              <tab.icon size={16} />
              <span>{tab.name}</span>
            </button>
          ))}

          {/* “Add New Policy” Button — Inline, aligned to right */}
          {!IsEmployee && (
            <StyledAddButton
              size="medium"
              onClick={() => setActiveTab('new')}
              aria-label="add new policy"
              sx={{
                minWidth: isLargeScreen ? '220px' : '200px',
                marginLeft: 'auto', // Pushes it to the far right
                height: '40px', // Explicitly match tab height
                flexShrink: 0, // Prevent squishing on small screens
                display: { xs: 'none', sm: 'flex' }, // Hide on extra small screens if needed
              }}
            >
              <FilePen size={18} className="policy-icon" />
              Add New Policy
            </StyledAddButton>
          )}
        </div>

        {/* Render Active Tab Content */}
        {activeTab === 'def' && <PolicyTypesComponent />}
        {activeTab === 'opolicies' && <OfficePoliciesPage />}
        {activeTab === 'hrHandbook' && !IsEmployee && <HRHandbookPage />}
        {activeTab === 'new' && !IsEmployee && <AddPolicyPage />}
      </div>
    </div>
  );
};

export default PoliciesManagement;